import { useMutation, useQuery } from 'react-query';
import workorderPecaAiServices from './workorder-pecaAi.services';
import {
  WorkOrderPecaAiBasketCombined,
  WorkOrderPecaAiBasketDetailRequest,
  WorkOrderPecaAiOrderDetailRequest,
} from './workorder-pecaAi.types';
import notificationError from '@/utils/functions/notificationError';
import { AxiosError } from 'axios';
import { notification } from 'antd';

export const WORKORDER_PECA_AI_HAS_BUYER = 'workorder/peca-ai/has-buyer';
export const WORKORDER_PECA_AI_BASKET_DETAIL =
  'workorder/peca-ai/basket/detail';
export const WORKORDER_PECA_AI_ORDER_DETAIL = 'workorder/peca-ai/order/detail';
export const WORKORDER_PECA_AI_VERIFY_TOKEN = 'workorder/peca-ai/verify-token';

export function useWorkOrderPecaAiHasBuyer(cnpj?: string, enabled = true) {
  return useQuery({
    queryKey: [WORKORDER_PECA_AI_HAS_BUYER, cnpj],
    queryFn: () => workorderPecaAiServices.hasBuyer(cnpj as string),
    enabled: !!cnpj && enabled,
  });
}

export function useWorkOrderPecaAiBasketDetail(
  params: WorkOrderPecaAiBasketDetailRequest,
  enabled = true,
) {
  const safe = <T>(p: PromiseSettledResult<T>) =>
    p.status === 'fulfilled' ? p.value : null;

  return useQuery<WorkOrderPecaAiBasketCombined>(
    [WORKORDER_PECA_AI_BASKET_DETAIL, { ...params, enabled }],
    async () => {
      const [detail, summary] = await Promise.allSettled([
        workorderPecaAiServices.detailBasket(params),
        workorderPecaAiServices.basketSummary(params),
      ]);

      return {
        detail: safe(detail),
        summary: safe(summary),
      };
    },
    {
      enabled: enabled && !!params.basketId && !!params.document,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useWorkOrderPecaAiOrderDetail(
  params: WorkOrderPecaAiOrderDetailRequest,
  enabled = true,
) {
  return useQuery({
    queryKey: [WORKORDER_PECA_AI_ORDER_DETAIL, { ...params }],
    queryFn: () => workorderPecaAiServices.detailOrder(params),
    onError: (error) => {
      const erro = error as AxiosError;

      if (!!erro.response?.status) return;

      notificationError(error);
    },
    enabled,
    retry: false,
  });
}

export function useWorkOrderPecaAiSearchProduct() {
  return useMutation(workorderPecaAiServices.searchProduct, {
    onError(err) {
      notificationError(err);
    },
  });
}

export function useWorkOrderPecaAiVerifyToken(enabled = true) {
  return useQuery(
    [WORKORDER_PECA_AI_VERIFY_TOKEN],
    () => workorderPecaAiServices.verifyToken(),
    {
      enabled: enabled,
      onError(err) {
        notificationError(err);
      },
    },
  );
}

export function useWorkOrderPecaAiAddProductInBasket() {
  return useMutation({
    mutationFn: workorderPecaAiServices.addProductInBasket,
    onSuccess() {
      notification.success({ message: 'Produto adicionado a cesta' });
    },
    onError(error) {
      notificationError(error);
    },
  });
}

export function useWorkOrderPecaAiRemoveProductInBasket() {
  return useMutation({
    mutationFn: workorderPecaAiServices.removeProductInBasket,
    onSuccess() {
      notification.success({ message: 'Produto removido a cesta' });
    },
    onError(error) {
      notificationError(error);
    },
  });
}
